/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { DevTool } from '@hookform/devtools';
import { UiStateContext } from '../../context/UiStateContext';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useIsSticky } from '../../hooks/useIsSticky';
import { SearchCategory } from '../../types/enums';
import { Kfh_PropertySearchMetaType, Kfh_SearchFacetType } from '../../types/graphqlTypes';
import { CurrencyCodeType, getCurrencyName } from '../../utils/getCurrencyName';
import { getOptions } from '../../utils/getOptions';
import { Button } from '../Button';
import { AltSelect } from '../forms/AltSelect';
import { Filters } from '../icon/icons/Filters';
import { Section } from '../Section';
import { LocationSearch } from './LocationSearch';
import { Skeleton, StyleSkeletonOverlay } from '../Skeleton';
import { useHasMounted } from '../../hooks/useHasMounted';
import { usePriceOptions, Option } from '../../hooks/usePriceOptions';

interface PropertySearchProps {
  parameters: Kfh_PropertySearchMetaType;
  facets: Kfh_SearchFacetType;
  category: SearchCategory;
  multiSearch?: { multiSearch: string; name: string };
  searchType?: string;
  activeFiltersCount: number;
  isLoading?: boolean;
}

export const StyledPropertySearchSection = styled(Section)<{ isSticky: boolean; ref: any }>`
  ${({ theme: { layers, shadows, space, media }, isSticky }) => css`
    position: sticky;
    top: 0;
    z-index: ${layers.search};
    padding-top: ${space.md}; // cls fix
    padding-bottom: ${space.md}; // cls fix

    ${isSticky &&
    css`
      box-shadow: ${shadows.medium};
    `}
  `};
`;

export const StyledPropertySearch = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: ${space.sm};
  `}
`;

const StyledInlineFilters = styled.div`
  ${({ theme: { space, media } }) => css`
    display: none;

    @media (min-width: ${media.md}) {
      display: flex;
      flex-grow: 1;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: ${space.sm};

      > *:nth-child(n + 3) {
        display: none;
      }
    }

    @media (min-width: ${media.lg}) {
      > *:nth-child(n + 3) {
        display: block;
      }
    }
  `}
`;

const StyledLocationSearch = styled(LocationSearch)`
  ${({ theme: { space, media } }) => css`
    flex-grow: 1;
    @media (min-width: ${media.xl}) {
      min-width: 342px;
    }
  `}
`;

export const PropertySearch: FC<PropertySearchProps> = ({
  parameters,
  facets,
  category,
  multiSearch,
  searchType,
  activeFiltersCount,
  isLoading,
}) => {
  const { register, watch, control, setValue } = useFormContext();
  const hasMounted = useHasMounted();

  const filterBladeTriggerRef = useRef<HTMLButtonElement>();
  const { current } = useCurrencies();

  const { setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);

  const { isSticky, stickyRef } = useIsSticky();
  // Filter blade control

  const handleOpenFilters = () => {
    setActiveBlade('propertyFilters');
    setActiveBladeTriggerEl(filterBladeTriggerRef);
  };

  const currencySaleType = category.toLowerCase();
  const currencyName = getCurrencyName(current?.code as CurrencyCodeType);

  const propertyTypes =
    facets?.propertyType?.length >= 1
      ? facets.propertyType.map(({ value, facet }) => ({ value: value || facet, name: facet }))
      : [];

  const currentPriceLowest = watch('priceLowest');
  const currentPriceHighest = watch('priceHighest');

  const bedrooms = watch('bedrooms');
  const propertyType = watch('propertyType');

  const priceLowest = useMemo(
    () =>
      parameters?.[`${currencySaleType}LowestPrice${currencyName}`].filter(({ value }) => value <= currentPriceHighest),
    [currencyName, currencySaleType, currentPriceHighest, parameters]
  );
  const priceHighest = useMemo(
    () =>
      parameters?.[`${currencySaleType}HighestPrice${currencyName}`].filter(({ value }) => value >= currentPriceLowest),
    [currencyName, currencySaleType, currentPriceLowest, parameters]
  );

  return (
    <StyledPropertySearchSection
      isSticky={isSticky}
      contained
      tight
      disablePaddingTop
      ref={stickyRef}
      className="section--white"
    >
      <DevTool control={control} />
      {(!hasMounted || isLoading) && (
        <StyleSkeletonOverlay height="64px" isSticky={isSticky}>
          <Skeleton height="64px" />
        </StyleSkeletonOverlay>
      )}
      <StyledPropertySearch>
        <StyledLocationSearch category={category} defaultMultiValue={multiSearch?.name} />

        <StyledInlineFilters>
          <AltSelect
            id="priceLowest"
            label="Min price"
            {...register('priceLowest', {
              valueAsNumber: true,
            })}
            options={getOptions(priceLowest)}
            constrained
          />
          <AltSelect
            id="priceHighest"
            label="Max price"
            {...register('priceHighest', {
              valueAsNumber: true,
            })}
            options={getOptions(priceHighest)}
            constrained
          />

          <Controller
            name="bedrooms"
            control={control}
            render={({ field }) => (
              <AltSelect
                id="bedrooms"
                label="Bedrooms"
                {...field}
                onChange={(event) => field.onChange([`${event.target.value}`])}
                options={[
                  { value: '', name: bedrooms?.length >= 2 ? 'Multiple' : 'All' },
                  ...getOptions(parameters?.bedroomsMinimum.map((v) => ({ ...v, value: v.value.toString() }))),
                ]}
                constrained
              />
            )}
          />

          {propertyTypes.length >= 1 && (
            <Controller
              name="propertyType"
              control={control}
              render={({ field }) => (
                <AltSelect
                  id="propertyType"
                  label="Property type"
                  {...field}
                  options={[{ value: '', name: propertyType?.length >= 2 ? 'Multiple' : 'All' }, ...propertyTypes]}
                  constrained
                />
              )}
            />
          )}
        </StyledInlineFilters>

        <Button
          onClick={handleOpenFilters}
          type="button"
          ref={filterBladeTriggerRef}
          iconRight={<Filters />}
          count={activeFiltersCount}
        >
          Filters
        </Button>
      </StyledPropertySearch>
    </StyledPropertySearchSection>
  );
};
